import { Button, Modal } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Column, ReadOnlyItem, Row, Text, TextType } from '@ydistri/ds';
import { useLoadCalculationToTemplateMutation } from '../apiCalculations';
import { addToast } from '@ydistri/utils';
import { useAppDispatch } from '../../../store';
import { Overlay } from '../../../components/global/Overlay';
import OverlayMessage, {
  OverlayMessageIcon,
} from '../../../components/global/ContentOverlay/OverlayComponents';
import { useGetCalculationQuery } from '../../CalculationDetail/apiCalculationDetail';
import { CalculationMode } from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import CreateCalculationWithDefaultsModal from '../components/CreateCalculationWithDefaultsModal/CreateCalculationWithDefaultsModal';
import { COLORS } from '@ydistri/theme';

const MODAL_LABEL_WIDTH = '10rem';
const loadingOverlayContent = <OverlayMessage title="Loading" icon={OverlayMessageIcon.SPINNER} />;

const ResultContainer = styled(Column)`
  max-height: 300px;
  overflow-y: auto;
  margin-top: 1rem;
  flex-wrap: nowrap;
`;

type ModalStatus = 'ready' | 'running' | 'done';
type CalculationItemStatus = 'preview' | 'success' | 'error';

interface CalculationItem {
  calculationId: number;
  calculationTitle: string;
  templateId?: number;
  templateTitle?: string;
  status: CalculationItemStatus;
  message: string;
}

const ResultItem = styled(Row)<{ $status: CalculationItemStatus }>`
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  justify-content: space-between;
  gap: 2rem;
  background-color: ${props => {
    switch (props.$status) {
      case 'preview':
        return COLORS.GREY_LIGHT;
      case 'success':
        return COLORS.BAR_HEALTHY;
      case 'error':
        return COLORS.BAR_DEADSTOCK_FADED;
    }
  }};
`;

const textTypeBasedOnStatus: Record<CalculationItemStatus, TextType> = {
  preview: 'default',
  success: 'success',
  error: 'danger',
};

interface LoadSubmergedIntoTemplateModalProps {
  calculationId: number;
  calculationTitle: string;
  onConfirmed?: () => void;
  onCanceled: () => void;
}

const LoadSubmergedIntoTemplateModal: React.FC<LoadSubmergedIntoTemplateModalProps> = ({
  calculationId,
  calculationTitle,
  onCanceled,
}) => {
  const dispatch = useAppDispatch();

  const [modalStatus, setModalStatus] = useState<ModalStatus>('ready');
  const [isLoading, setIsLoading] = useState(false);
  const [calculationItems, setCalculationItems] = useState<CalculationItem[]>([]);
  const [showCreateCalculationModal, setShowCreateCalculationModal] = useState(false);
  const [loadCalculationToTemplate] = useLoadCalculationToTemplateMutation();

  const { data: calculation, isFetching: isCalculationFetching } =
    useGetCalculationQuery(calculationId);

  const cancelHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onCanceled();
    },
    [onCanceled],
  );

  const loadSubmergedCalculations = useCallback(async () => {
    if (!calculation?.subordinateCalculations) {
      return;
    }

    setIsLoading(true);
    const newItems: CalculationItem[] = [];

    if (calculation.subordinateCalculations.length === 0) {
      setCalculationItems([
        {
          calculationId: 0,
          calculationTitle: '',
          status: 'error',
          message: 'No valid submerged calculations found with templates.',
        },
      ]);
      setIsLoading(false);
      return;
    }

    // Process each submerged calculation
    for (const submergedCalc of calculation.subordinateCalculations) {
      try {
        if (submergedCalc.sourceTemplate) {
          await loadCalculationToTemplate({
            fromCalculationId: submergedCalc.id,
            toTemplateId: submergedCalc.sourceTemplate.id,
          }).unwrap();

          newItems.push({
            calculationId: submergedCalc.id,
            calculationTitle: submergedCalc.title,
            templateId: submergedCalc.sourceTemplate.id,
            templateTitle: submergedCalc.sourceTemplate.title,
            status: 'success',
            message: `Successfully loaded into template "${submergedCalc.sourceTemplate.title}"`,
          });
        } else {
          let templateTitle = 'Not found';
          let message = `No source template found for this calculation`;
          if (submergedCalc.mode === CalculationMode.Manual) {
            templateTitle = 'Not applicable';
            message = `This calculation is in manual mode and cannot be loaded into a template`;
          }
          newItems.push({
            calculationId: submergedCalc.id,
            calculationTitle: submergedCalc.title,
            templateId: 0,
            templateTitle,
            status: 'error',
            message,
          });
        }
      } catch {
        newItems.push({
          calculationId: submergedCalc.id,
          calculationTitle: submergedCalc.title,
          templateId: submergedCalc.sourceTemplate?.id,
          templateTitle: submergedCalc.sourceTemplate?.title,
          status: 'error',
          message: `Failed to load.`, // ${(error as ErrorType).response?.data?.Messages?.join(', ') || 'Unknown error'}
        });
      }
    }

    setCalculationItems(newItems);
    setIsLoading(false);
    setModalStatus('done');

    // Show a summary toast
    const successCount = newItems.filter(item => item.status === 'success').length;
    dispatch(
      addToast({
        message: `Loaded ${successCount} of ${calculation.subordinateCalculations.length} submerged calculations into their templates`,
        isError: successCount === 0 && calculation.subordinateCalculations.length > 0,
      }),
    );
  }, [calculation, loadCalculationToTemplate, dispatch]);

  // Generate calculation items when calculation data is loaded
  const generateCalculationItems = useCallback(() => {
    if (!calculation?.subordinateCalculations) {
      return;
    }

    const newItems: CalculationItem[] = [];

    if (calculation.subordinateCalculations.length === 0) {
      setCalculationItems([
        {
          calculationId: 0,
          calculationTitle: '',
          status: 'preview',
          message: 'No valid submerged calculations found with templates.',
        },
      ]);
      return;
    }

    // Process each submerged calculation for preview
    for (const submergedCalc of calculation.subordinateCalculations) {
      if (submergedCalc.sourceTemplate) {
        newItems.push({
          calculationId: submergedCalc.id,
          calculationTitle: submergedCalc.title,
          templateId: submergedCalc.sourceTemplate.id,
          templateTitle: submergedCalc.sourceTemplate.title,
          status: 'preview',
          message: `Template: ${submergedCalc.sourceTemplate.title}`,
        });
      } else {
        let templateTitle = 'Not found';
        let message = `No source template found for this calculation`;
        if (submergedCalc.mode === CalculationMode.Manual) {
          templateTitle = 'Not applicable';
          message = `This calculation is in manual mode and cannot be loaded into a template`;
        }
        newItems.push({
          calculationId: submergedCalc.id,
          calculationTitle: submergedCalc.title,
          templateId: 0,
          templateTitle,
          status: 'preview',
          message,
        });
      }
    }

    setCalculationItems(newItems);
  }, [calculation]);

  // Generate calculation items when calculation data is loaded
  useEffect(() => {
    if (calculation && !isCalculationFetching) {
      generateCalculationItems();
    }
  }, [calculation, isCalculationFetching, generateCalculationItems]);

  const continueHandler = useCallback(() => {
    if (modalStatus === 'ready' && calculation) {
      setModalStatus('running');
      void loadSubmergedCalculations();
    } else if (modalStatus === 'done') {
      onCanceled();
    }
  }, [modalStatus, calculation, loadSubmergedCalculations, onCanceled]);

  const createCalculationHandler = useCallback(() => {
    setShowCreateCalculationModal(true);
  }, []);

  const closeCreateCalculationModal = useCallback(() => {
    setShowCreateCalculationModal(false);
    onCanceled();
  }, [onCanceled]);

  // The modal is wrapped in a div to prevent the click event from bubbling up to the parent
  const onWrapperClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);

  const footer = useMemo(() => {
    let continueButtonText = 'Continue';
    if (modalStatus === 'running') {
      continueButtonText = 'Loading...';
    } else if (modalStatus === 'done') {
      continueButtonText = '';
    }

    return (
      <>
        <Button onClick={cancelHandler}>Close</Button>
        {modalStatus === 'done' && (
          <Button type="primary" onClick={createCalculationHandler}>
            Create calculation
          </Button>
        )}
        {continueButtonText !== '' && (
          <Button type="primary" disabled={modalStatus === 'running'} onClick={continueHandler}>
            {continueButtonText}
          </Button>
        )}
      </>
    );
  }, [modalStatus, cancelHandler, continueHandler, createCalculationHandler]);

  return (
    <div onClick={onWrapperClick}>
      <Modal
        data-type="modal"
        open={true}
        title="Load submerged calculations"
        closable={false}
        onCancel={cancelHandler}
        footer={footer}
        maskClosable={false}
        width="60rem"
      >
        <Column>
          <Row>
            <Text>
              Review the calculations below and click &quot;Continue&quot; to load them into their
              respective templates.
            </Text>
          </Row>
          <Overlay
            active={isCalculationFetching || isLoading}
            overlayContent={loadingOverlayContent}
          >
            <Column $padding={4} $gap="1rem">
              <ReadOnlyItem
                label="Source calculation"
                labelWidth={MODAL_LABEL_WIDTH}
                value={`#${calculationId}: ${calculationTitle}`}
              />
              {calculationItems.length > 0 && (
                <>
                  <ReadOnlyItem
                    label={modalStatus === 'ready' ? 'Calculations to load' : 'Results'}
                    labelWidth={MODAL_LABEL_WIDTH}
                    value=""
                  />
                  <ResultContainer>
                    {calculationItems.map(item => {
                      const textType = textTypeBasedOnStatus[item.status];

                      return (
                        <ResultItem key={item.calculationId} $status={item.status}>
                          {item.calculationId > 0 ? (
                            <>
                              <Text $type={textType} $bold={true}>
                                #{item.calculationId}: {item.calculationTitle}
                              </Text>
                              <Text $type={textType}>{item.message}</Text>
                            </>
                          ) : (
                            <Text $type="danger">{item.message}</Text>
                          )}
                        </ResultItem>
                      );
                    })}
                  </ResultContainer>
                </>
              )}
            </Column>
          </Overlay>
        </Column>
      </Modal>
      {showCreateCalculationModal && calculation && (
        <CreateCalculationWithDefaultsModal
          calculation={calculation}
          onClose={closeCreateCalculationModal}
        />
      )}
    </div>
  );
};

export default LoadSubmergedIntoTemplateModal;
